import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie } from "lucide-react";
import { useLang } from "@/i18n";
import { grantAdsConsent } from "@/lib/gtag";
import { PolicyDialog } from "./PolicyDialog";

export const MobileBottomSpacer = () => {
  const [decided, setDecided] = useState(() => !!localStorage.getItem("dds-cookie-consent"));

  useEffect(() => {
    const fn = () => setDecided(true);
    window.addEventListener("dds-cookie-decided", fn);
    return () => window.removeEventListener("dds-cookie-decided", fn);
  }, []);

  return <div className={`${decided ? "h-14" : "h-[11rem]"} md:hidden`} />;
};

export const CookieBanner = () => {
  const { t } = useLang();
  const [visible, setVisible] = useState(() => !localStorage.getItem("dds-cookie-consent"));
  const [policyOpen, setPolicyOpen] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("dds-cookie-consent") === "all") grantAdsConsent();
  }, []);

  const decide = (v) => {
    localStorage.setItem("dds-cookie-consent", v);
    if (v === "all") grantAdsConsent();
    window.dispatchEvent(new Event("dds-cookie-decided"));
    setVisible(false);
  };

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-[3.5rem] left-2 right-2 z-[100] max-h-[110px] border border-ivory/15 bg-ink p-3 text-ivory shadow-2xl md:bottom-6 md:left-auto md:right-6 md:max-h-none md:max-w-md md:p-6"
            data-testid="cookie-banner"
          >
            <div className="hidden items-center gap-2.5 md:flex">
              <Cookie className="h-4 w-4 text-sun" strokeWidth={1.5} />
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-sun">{t.cookieBanner.title}</p>
            </div>
            <p className="text-xs leading-snug text-ivory/80 md:mt-3 md:text-sm md:leading-relaxed">
              <span className="md:hidden">{t.cookieBanner.short}</span>
              <span className="hidden md:inline">{t.cookieBanner.text}</span>{" "}
              <button
                onClick={() => setPolicyOpen(true)}
                className="underline underline-offset-2 transition-colors hover:text-sun"
                data-testid="cookie-banner-policy-link"
              >
                <span className="md:hidden">Cookie Policy</span>
                <span className="hidden md:inline">{t.cookieBanner.more}</span>
              </button>
            </p>
            <div className="mt-2.5 grid grid-cols-2 gap-2 md:mt-5 md:flex md:flex-wrap md:gap-3">
              <button
                onClick={() => decide("all")}
                data-testid="cookie-accept"
                className="rounded-full bg-terracotta px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-ivory transition-colors duration-300 hover:bg-sun hover:text-ink md:px-6 md:py-2.5 md:tracking-[0.18em]"
              >
                {t.cookieBanner.accept}
              </button>
              <button
                onClick={() => decide("necessary")}
                data-testid="cookie-necessary"
                className="rounded-full border border-ivory/30 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-ivory/85 transition-colors duration-300 hover:border-ivory hover:text-ivory md:px-6 md:py-2.5 md:tracking-[0.18em]"
              >
                {t.cookieBanner.necessary}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <PolicyDialog open={policyOpen} onOpenChange={setPolicyOpen} type="cookie" />
    </>
  );
};
